"use client";
import { FormEvent, useRef, useState } from "react";
import Link from "next/link";
import { PatternFormat } from "react-number-format";
import { validatePhoneNumber } from "../utils/phone-validation";
import { useSubmitLock } from "../utils/useSubmitLock";
import { submitLead } from "../utils/lead-submission";
import { Captcha, type CaptchaHandle } from "./Captcha";

interface FormSubmitProps {
  placeholder?: string;
  text?: string;
  className?: string;
}

export default function FormSubmit({ placeholder, text, className }: FormSubmitProps) {
  const captchaRef = useRef<CaptchaHandle>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [msg, setMsg] = useState("");
  const [phone, setPhone] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [nameErrorMessage, setNameErrorMessage] = useState("");
  const [surnameErrorMessage, setSurnameErrorMessage] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [messageErrorMessage, setMessageErrorMessage] = useState("");
  const { submitting, tryStart, finish } = useSubmitLock();

  const input_styles = "bg-white p-1/2 dt:px-[.75rem] dt:py-[.625rem] mb-1/2 text-1 leading-1 dt:text-1.5 dt:leading-1.5 w-full rounded-1/2 font-normal border-0";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


  const setPhone_ = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    setPhone(value);

    if (digitsOnly === "") {
      setPhoneErrorMessage("Phone number cannot be blank");
      return;
    }

    if (digitsOnly.length < 10) {
      setPhoneErrorMessage("");
      return;
    }

    if (digitsOnly.length === 10) {
      setPhoneErrorMessage("");
      void validatePhone(value);
    }
  };

  const setEmail_ = (value: string) => {
    if (value === "") {
      setErrorMessage("Email cannot be blank");
    } else {
      setErrorMessage("");
    }
    setEmail(value);
  }

  const setName_ = (value: string) => {
    if (value === "") {
      setNameErrorMessage("First name cannot be blank");
    } else {
      setNameErrorMessage("");
    }
    setName(value);
  }

  const setSurname_ = (value: string) => {
    setSurnameErrorMessage(value.trim() ? "" : "Last name cannot be blank");
    setSurname(value);
  };

  const setMsg_ = (value: string) => {
    if (value === "") {
      setMessageErrorMessage("Message cannot be blank");
    } else {
      setMessageErrorMessage("");
    }
    setMsg(value);
  }

  async function validatePhone(value: string): Promise<boolean> {
    const digitsOnly = value.replace(/\D/g, "");

    if (digitsOnly.length === 0) {
      setPhoneErrorMessage("Phone number cannot be blank");
      return false;
    }

    if (digitsOnly.length !== 10) {
      setPhoneErrorMessage("Phone numbers must have 10 digits");
      return false;
    }

    const result = await validatePhoneNumber(digitsOnly);
    if (result === "invalid") {
      setPhoneErrorMessage("Invalid phone number");
      return false;
    }

    setPhoneErrorMessage("");
    return true;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!tryStart()) {
      return;
    }

    const digitsOnlyPhone = phone.replace(/\D/g, "");
    const captchaInstance = captchaRef.current;
    setSubmissionError("");

    try {
      const hasBasicErrors =
        email === "" ||
        name.trim() === "" ||
        surname.trim() === "" ||
        msg.trim() === "" ||
        digitsOnlyPhone.length === 0 ||
        !emailRegex.test(email) ||
        digitsOnlyPhone.length !== 10;

      if (hasBasicErrors) {
        if (digitsOnlyPhone.length === 0) {
          setPhoneErrorMessage("Phone number cannot be blank");
        } else if (digitsOnlyPhone.length !== 10) {
          setPhoneErrorMessage("Phone numbers must have 10 digits");
        }

        if (email === "") {
          setErrorMessage("Email cannot be blank");
        } else if (!emailRegex.test(email)) {
          setErrorMessage("Enter a valid email address");
        }

        if (name.trim() === "") {
          setNameErrorMessage("First name cannot be blank");
        }
        if (surname.trim() === "") {
          setSurnameErrorMessage("Last name cannot be blank");
        }
        if (msg.trim() === "") {
          setMessageErrorMessage("Message cannot be blank");
        }

        return;
      }

      const isPhoneValid = await validatePhone(phone);
      if (!isPhoneValid) {
        return;
      }

      if (!captchaInstance) {
        setSubmissionError("The captcha is not ready. Please reload the page and try again.");
        return;
      }

      let captchaToken: string;
      try {
        captchaToken = await captchaInstance.getToken();
      } catch {
        setSubmissionError("We couldn't verify the captcha. Please reload the page and try again.");
        return;
      }
      try {
        await submitLead({
          kind: "contact",
          firstName: name,
          lastName: surname,
          email,
          phone: digitsOnlyPhone,
          message: msg,
        }, captchaToken);
      } catch (error) {
        setSubmissionError(error instanceof Error ? error.message : "We couldn't submit your request.");
        return;
      }

      setErrorMessage("");
      setSuccessMessage("Request successfully sent!");
      setPhoneErrorMessage("");
      setNameErrorMessage("");
      setSurnameErrorMessage("");
      setMessageErrorMessage("");
      setEmail("");
      setName("");
      setSurname("");
      setPhone("");
      setMsg("");
    } finally {
      captchaInstance?.reset();
      finish();
    }
  }

  return (
    <div className={`flex mx-auto py-3 w-[66.67%] ${className ? className : 'dt:mt-6'}`}>
      <div className="flex flex-col w-full">
        <form className="flex w-full" id="contactForm" noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Captcha ref={captchaRef} />
          {successMessage ? (
            <p className="text-center w-full text-1 leading-1 dt:text-1.5 dt:leading-1.5  text-white mx-auto">
              Thank you!<br /><br />Your request has been sent.<br />Our team will follow up with you.<br /><br />
              <Link href="/" className="uppercase font-bold mt-1 text-3/4 leading-3/4 dt:text-1 dt:leading-1 mx-auto underline-offset-1/4 text-white hover:underline">Back to homepage</Link>
            </p>
          ) : (
            <div className="flex flex-col w-full relative">
              <h4 className="mb-1/2 uppercase font-bold text-3/4 leading-1 dt:text-1 dt:leading-1.5 text-white tracking-widest">Personal details</h4>
              <div className="grid grid-cols-2 gap-x-1/2">
                <div>
                  <input
                    type="text"
                    name="first_name"
                    aria-label="First name"
                    placeholder="First name"
                    maxLength={40}
                    onChange={(e) => setName_(e.target.value)}
                    value={name}
                    className={`${input_styles} ${nameErrorMessage && 'text-red placeholder:text-red'}`}
                  />
                  {nameErrorMessage && <p className="text-white">{nameErrorMessage}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    name="last_name"
                    aria-label="Last name"
                    placeholder="Last name"
                    maxLength={80}
                    onChange={(e) => setSurname_(e.target.value)}
                    value={surname}
                    className={`${input_styles} ${surnameErrorMessage && 'text-red placeholder:text-red'}`}
                  />
                  {surnameErrorMessage && <p className="text-white">{surnameErrorMessage}</p>}
                </div>
              </div>
              <input
                type="email"
                name="email"
                aria-label="Email"
                maxLength={80}
                placeholder={placeholder ?? 'Email'}
                onChange={(e) => setEmail_(e.target.value)}
                value={email}
                className={`${input_styles} ${errorMessage && 'text-red placeholder:text-red'}`}
              />

              {errorMessage && (
                <p className="text-white">
                  {errorMessage}
                </p>
              )}
              <PatternFormat
                name="phone"
                type="tel"
                format="(###) ###-####"
                mask="_"
                placeholder={"Phone number"}
                value={phone}
                className={`${input_styles} ${phoneErrorMessage && 'text-red placeholder:text-red'}`}

                //  onKeyDown={(event) => {return (event.charCode !=8 && event.charCode ==0 || (event.charCode >= 48 && event.charCode <= 57))}}
                onValueChange={value => setPhone_(value.formattedValue)}
                required
              />

              {phoneErrorMessage && (
                <p className="text-white ">{phoneErrorMessage}</p>
              )}
              <h4 className="mb-1/2 mt-1 uppercase font-bold text-3/4 leading-1 dt:text-1 dt:leading-1.5 text-white tracking-widest">Message</h4>
              <textarea
                name="description"
                aria-label="Message"
                maxLength={32000}
                onChange={(e) => setMsg_(e.target.value)}
                value={msg}
                placeholder="Type your message here"
                className={`${input_styles} ${messageErrorMessage && 'text-red placeholder:text-red'} h-[9rem] resize-none`}
              />
              {messageErrorMessage && (
                <p className="text-white">{messageErrorMessage}</p>
              )}
              {submissionError && <p role="alert" className="text-white">{submissionError}</p>}
              <div className="flex justify-end">
                <button
                  aria-label="Submit form"
                  type="submit"
                  disabled={submitting === true}
                  className={`${submitting === true ? 'bg-[#7AA4D0]' : 'bg-blue'} w-full text-white px-1/2 dt:px-1.5 py-1/2 dt:py-1 mt-1.5 dt:mt-2.5 rounded-1/2 uppercase font-bold text-1 leading-1 tracking-wider`}
                >
                  {submitting === false ? (text ?? 'Submit') : 'Submitting...'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
